import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from 'components/ui/Header';
import WhatsAppChat from 'components/ui/WhatsAppChat';
import CallButton from 'components/ui/CallButton';
import BookingSummaryPanel from 'components/ui/BookingSummaryPanel';
import ActivityCard from './components/ActivityCard';

import ActivityFilters from './components/ActivityFilters';
import BookingPanel from './components/BookingPanel';
import AvailabilityCalendar from './components/AvailabilityCalendar';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const ActivitiesExperiences = () => {
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showBookingPanel, setShowBookingPanel] = useState(false);
  const [showBookingSummary, setShowBookingSummary] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [currentAttractionIndex, setCurrentAttractionIndex] = useState(0);
  const [filters, setFilters] = useState({
    type: 'all',
    difficulty: 'all',
    duration: 'all',
    priceRange: 'all',
    sortBy: 'popularity',
    familyFriendly: false,
    availableToday: false,
    seasonal: false,
    groupDiscount: false
  });

  // Mock activities data
  const activitiesData = [
    {
      id: 1,
      name: "White-Water Rafting",
      description: `Experience the thrill of navigating through the rapids of River Kali. Our expert guides ensure safety while you enjoy the adrenaline rush of conquering Grade II and III rapids.\n\nThis adventure includes professional safety equipment, experienced guides, and stunning views of the Western Ghats. Perfect for adventure seekers looking for an unforgettable experience.`,
      images: [
        "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop"
      ],
      price: 1200,
      originalPrice: 1500,
      duration: "3-4 hours",
      difficulty: "Moderate",
      groupSize: "6-12 people",
      available: true,
      seasonal: "Best Oct-May",
      features: ["Professional Guide", "Safety Equipment", "Lunch Included", "Photography"],
      safetyRequirements: [
        "Minimum age 12 years",
        "Basic swimming knowledge required",
        "No heart conditions or pregnancy",
        "Weight limit: 100kg maximum"
      ],
      category: "water-sports"
    },
    {
      id: 2,
      name: "Jungle Safari",
      description: `Explore the rich biodiversity of Dandeli Wildlife Sanctuary on our guided jungle safari. Spot exotic wildlife including black panthers, elephants, deer, and over 200 bird species.\n\nOur experienced naturalists will help you understand the ecosystem while ensuring minimal environmental impact. Early morning and evening safaris offer the best wildlife viewing opportunities.`,
      images: [
        "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1551969014-7d2c4cddf0b6?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1564760055775-d63b17a55c44?w=800&h=600&fit=crop"
      ],
      price: 800,
      duration: "2-3 hours",
      difficulty: "Easy",
      groupSize: "4-8 people",
      available: true,
      seasonal: "Year Round",
      features: ["Expert Naturalist", "Wildlife Spotting", "Bird Watching", "Nature Photography"],
      safetyRequirements: [
        "Suitable for all ages",
        "Wear earth-toned clothing",
        "No loud noises or sudden movements",
        "Follow guide instructions strictly"
      ],
      category: "wildlife"
    },
    {
      id: 3,
      name: "Kayaking Adventure",
      description: `Paddle through the serene backwaters of River Kali in our stable kayaks. Perfect for beginners and experienced paddlers alike, this peaceful journey offers stunning views of the surrounding forest.\n\nEnjoy the tranquility of nature while spotting various bird species and learning about the local ecosystem from our knowledgeable guides.`,
      images: [
        "https://images.unsplash.com/photo-1544551763-77ef2d0cfc6c?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop"
      ],
      price: 600,
      duration: "2 hours",
      difficulty: "Easy",
      groupSize: "2-10 people",
      available: true,
      seasonal: "Year Round",
      features: ["Single/Double Kayaks", "Safety Briefing", "Life Jackets", "Scenic Route"],
      safetyRequirements: [
        "Minimum age 8 years",
        "Basic swimming knowledge preferred",
        "No serious medical conditions",
        "Maximum weight 90kg per person"
      ],
      category: "water-sports"
    },
    {
      id: 4,
      name: "Night Campfire Experience",
      description: `Gather around a crackling campfire under the starlit sky for an evening of storytelling, local folk music, and traditional dance performances.\n\nEnjoy authentic local cuisine, participate in cultural activities, and create lasting memories with fellow travelers in the heart of nature.`,
      images: [
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1504851149312-7a075b496cc7?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=800&h=600&fit=crop"
      ],
      price: 400,
      duration: "3 hours",
      difficulty: "Easy",
      groupSize: "10-30 people",
      available: false,
      seasonal: "Oct-Mar",
      features: ["Live Music", "Folk Dance", "Local Cuisine", "Stargazing"],
      safetyRequirements: [
        "Suitable for all ages",
        "Bring warm clothing",
        "No alcohol allowed",
        "Respect local customs"
      ],
      category: "cultural"
    },
    {
      id: 5,
      name: "Rock Climbing",
      description: `Challenge yourself on the natural rock formations of Dandeli. Our certified instructors provide comprehensive training and safety equipment for climbers of all skill levels.\n\nExperience the thrill of conquering vertical challenges while enjoying panoramic views of the Western Ghats landscape.`,
      images: [
        "https://images.unsplash.com/photo-1522163182402-834f871fd851?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop"
      ],
      price: 1000,
      duration: "4 hours",
      difficulty: "Challenging",
      groupSize: "4-8 people",
      available: true,
      seasonal: "Oct-May",
      features: ["Certified Instructor", "Safety Equipment", "Multiple Routes", "Achievement Certificate"],
      safetyRequirements: [
        "Minimum age 14 years",
        "Good physical fitness required",
        "No fear of heights",
        "Medical clearance for heart conditions"
      ],
      category: "adventure"
    },
    {
      id: 6,
      name: "Bird Watching Tour",
      description: `Discover the incredible avian diversity of Dandeli with our expert ornithologist guides. Spot over 200 species including the Great Hornbill, Malabar Pied Hornbill, and various endemic species.\n\nEarly morning tours offer the best opportunities for bird photography and behavior observation in their natural habitat.`,
      images: [
        "https://images.unsplash.com/photo-1444927714506-8492d94b5ba0?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop"
      ],
      price: 500,
      duration: "3 hours",
      difficulty: "Easy",
      groupSize: "4-12 people",
      available: true,
      seasonal: "Year Round",
      features: ["Expert Ornithologist", "Binoculars Provided", "Bird Checklist", "Photography Tips"],
      safetyRequirements: [
        "Suitable for all ages",
        "Wear comfortable walking shoes",
        "Bring sun protection",
        "Maintain silence during observation"
      ],
      category: "wildlife"
    }
  ];

  // Mock nearby attractions data
  const attractionsData = [
    {
      id: 1,
      name: "Dandeli Wildlife Sanctuary",
      description: `Spread across 834 square kilometers, this sanctuary is home to diverse flora and fauna including tigers, leopards, elephants, and over 200 bird species.\n\nThe sanctuary offers excellent opportunities for wildlife photography, nature walks, and understanding conservation efforts in the Western Ghats region.`,
      image: "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800&h=600&fit=crop",
      distance: "5 km",
      location: "Dandeli, Karnataka",
      visitDuration: "4-6 hours",
      category: "Wildlife Sanctuary",
      coordinates: { lat: 15.2593, lng: 74.6167 },
      highlights: [
        "Tiger and leopard sightings",
        "Over 200 bird species",
        "Elephant herds",
        "Crocodile spotting at Kali River",
        "Guided nature walks",
        "Photography opportunities"
      ],
      bestTime: "October to May for wildlife viewing, early morning and evening safaris recommended"
    },
    {
      id: 2,
      name: "Syntheri Rocks",
      description: `A magnificent natural rock formation created by the Kaneri River cutting through limestone rocks over millions of years.\n\nThe 300-foot deep valley offers breathtaking views and is perfect for photography enthusiasts and geology lovers.`,
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
      distance: "32 km",
      location: "Syntheri, Karnataka",
      visitDuration: "2-3 hours",
      category: "Natural Formation",
      coordinates: { lat: 15.3847, lng: 74.4958 },
      highlights: [
        "300-foot deep limestone valley",
        "Unique geological formations",
        "Kaneri River views",
        "Photography paradise",
        "Trekking opportunities",
        "Monsoon waterfalls"
      ],
      bestTime: "Post-monsoon (October to February) for clear views and pleasant weather"
    },
    {
      id: 3,
      name: "Kali River",
      description: `The lifeline of Dandeli, this pristine river offers numerous water activities and is perfect for nature lovers seeking tranquility.\n\nThe river supports diverse aquatic life and provides excellent opportunities for fishing, boating, and riverside camping.`,
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&h=600&fit=crop",
      distance: "2 km",
      location: "Dandeli, Karnataka",
      visitDuration: "3-4 hours",
      category: "River",
      coordinates: { lat: 15.2500, lng: 74.6333 },
      highlights: [
        "White-water rafting",
        "Kayaking and canoeing",
        "Fishing opportunities",
        "Riverside camping",
        "Crocodile spotting",
        "Scenic boat rides"
      ],
      bestTime: "October to May for water activities, monsoon season for scenic beauty"
    },
    {
      id: 4,
      name: "Supa Dam",
      description: `One of the major hydroelectric projects in Karnataka, this dam creates a beautiful reservoir surrounded by lush green hills.\n\nThe area offers boating facilities, fishing opportunities, and stunning sunset views over the backwaters.`,
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
      distance: "25 km",
      location: "Supa, Karnataka",
      visitDuration: "2-3 hours",
      category: "Dam & Reservoir",
      coordinates: { lat: 15.1667, lng: 74.5833 },
      highlights: [
        "Hydroelectric dam structure",
        "Scenic reservoir views",
        "Boating facilities",
        "Fishing opportunities",
        "Sunset photography",
        "Picnic spots"
      ],
      bestTime: "October to March for pleasant weather and clear reservoir views"
    }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      type: 'all',
      difficulty: 'all',
      duration: 'all',
      priceRange: 'all',
      sortBy: 'popularity',
      familyFriendly: false,
      availableToday: false,
      seasonal: false,
      groupDiscount: false
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters?.type !== 'all') count++;
    if (filters?.difficulty !== 'all') count++;
    if (filters?.duration !== 'all') count++;
    if (filters?.priceRange !== 'all') count++;
    if (filters?.familyFriendly) count++;
    if (filters?.availableToday) count++;
    if (filters?.seasonal) count++;
    if (filters?.groupDiscount) count++;
    return count;
  };

  const filterActivities = (activities) => {
    return activities?.filter(activity => {
      // Type filter
      if (filters?.type !== 'all' && activity?.category !== filters?.type) return false;
      
      // Difficulty filter
      if (filters?.difficulty !== 'all' && activity?.difficulty?.toLowerCase() !== filters?.difficulty) return false;
      
      // Duration filter
      if (filters?.duration !== 'all') {
        const duration = activity?.duration?.toLowerCase();
        switch (filters?.duration) {
          case 'short':
            if (!duration?.includes('2 hours') && !duration?.includes('1 hour')) return false;
            break;
          case 'medium':
            if (!duration?.includes('2-3') && !duration?.includes('3-4')) return false;
            break;
          case 'long':
            if (!duration?.includes('4') && !duration?.includes('5') && !duration?.includes('6')) return false;
            break;
          case 'full-day':
            if (!duration?.includes('8') && !duration?.includes('full')) return false;
            break;
        }
      }
      
      // Price range filter
      if (filters?.priceRange !== 'all') {
        switch (filters?.priceRange) {
          case 'budget':
            if (activity?.price >= 1000) return false;
            break;
          case 'mid':
            if (activity?.price < 1000 || activity?.price > 2500) return false;
            break;
          case 'premium':
            if (activity?.price <= 2500) return false;
            break;
        }
      }
      
      // Quick filters
      if (filters?.availableToday && !activity?.available) return false;
      if (filters?.seasonal && !activity?.seasonal) return false;
      
      return true;
    });
  };

  const sortActivities = (activities) => {
    const sorted = [...activities];
    switch (filters?.sortBy) {
      case 'price-low':
        return sorted?.sort((a, b) => a?.price - b?.price);
      case 'price-high':
        return sorted?.sort((a, b) => b?.price - a?.price);
      case 'duration':
        return sorted?.sort((a, b) => a?.duration?.localeCompare(b?.duration));
      case 'difficulty':
        const difficultyOrder = { 'Easy': 1, 'Moderate': 2, 'Challenging': 3 };
        return sorted?.sort((a, b) => difficultyOrder?.[a?.difficulty] - difficultyOrder?.[b?.difficulty]);
      default:
        return sorted;
    }
  };

  const filteredAndSortedActivities = sortActivities(filterActivities(activitiesData));

  const handleBookNow = (activity) => {
    setSelectedActivity(activity);
    setShowBookingPanel(true);
  };

  const handleBookingConfirm = (bookingDetails) => {
    console.log('Booking confirmed:', bookingDetails);
    setShowBookingPanel(false);
    setShowBookingSummary(true);
    // In a real app, this would integrate with payment gateway
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const nextAttraction = () => {
    setCurrentAttractionIndex((prev) => 
      prev === attractionsData?.length - 1 ? 0 : prev + 1
    );
  };

  const prevAttraction = () => {
    setCurrentAttractionIndex((prev) => 
      prev === 0 ? attractionsData?.length - 1 : prev - 1
    );
  };

  // Auto-rotate attractions every 5 seconds
  useEffect(() => {
    const interval = setInterval(nextAttraction, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Activities & Experiences - Dandeli Adventure Resorts</title>
        <meta name="description" content="Discover thrilling adventures and unique experiences at Dandeli Adventure Resorts. From white-water rafting to jungle safaris, create unforgettable memories in nature's paradise." />
        <meta name="keywords" content="Dandeli activities, adventure sports, jungle safari, white water rafting, kayaking, rock climbing, bird watching, nature experiences" />
      </Helmet>
      <Header />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920&h=1080&fit=crop')`
            }}
          />
          
          <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
            <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
              Adventures & Experiences
            </h1>
            <p className="text-lg md:text-xl mb-8 opacity-90">
              Discover thrilling adventures and create unforgettable memories in the heart of nature
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                onClick={() => document.getElementById('activities')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Book Activities
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="MapPin"
                iconPosition="left"
                className="text-white border-white hover:bg-white hover:text-primary"
                onClick={() => document.getElementById('attractions')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Attractions
              </Button>
            </div>
          </div>
        </section>

        {/* Activities Section */}
        <section id="activities" className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Featured Activities
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From adrenaline-pumping adventures to peaceful nature experiences, 
                discover activities that match your spirit of adventure
              </p>
            </div>

            {/* Filters */}
            <ActivityFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              activeFiltersCount={getActiveFiltersCount()}
            />

            {/* Activities Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredAndSortedActivities?.map((activity) => (
                <ActivityCard
                  key={activity?.id}
                  activity={activity}
                  onBookNow={handleBookNow}
                />
              ))}
            </div>

            {/* No Results */}
            {filteredAndSortedActivities?.length === 0 && (
              <div className="text-center py-12">
                <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  No activities found
                </h3>
                <p className="text-muted-foreground mb-4">
                  Try adjusting your filters to see more activities
                </p>
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Availability Calendar Section */}
        <section className="py-16 px-6 bg-muted">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Check Availability
              </h2>
              <p className="text-lg text-muted-foreground">
                Select your preferred date to see activity availability and pricing
              </p>
            </div>
            
            <AvailabilityCalendar
              onDateSelect={handleDateSelect}
              selectedDate={selectedDate}
            />
          </div>
        </section>

        {/* Nearby Attractions Section */}
        <section id="attractions" className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Nearby Attractions
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Explore the natural wonders and scenic destinations around Dandeli
              </p>
            </div>

            {/* Attractions Carousel */}
            <div className="relative">
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentAttractionIndex * 100}%)` }}
                >
                  {attractionsData?.map((attraction) => (
                    <div key={attraction?.id} className="w-full flex-shrink-0 px-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                        {/* Image */}
                        <div className="relative h-96 rounded-lg overflow-hidden">
                          <img
                            src={attraction?.image}
                            alt={attraction?.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-4 left-4 px-3 py-1 bg-primary text-primary-foreground text-sm font-medium rounded-md">
                            {attraction?.category}
                          </div>
                          <div className="absolute top-4 right-4 px-3 py-1 bg-background/90 backdrop-blur-sm text-foreground text-sm font-medium rounded-md">
                            {attraction?.distance} away
                          </div>
                        </div>

                        {/* Content */}
                        <div className="space-y-6">
                          <div>
                            <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                              {attraction?.name}
                            </h3>
                            <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-4">
                              <div className="flex items-center space-x-1">
                                <Icon name="MapPin" size={16} />
                                <span>{attraction?.location}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Icon name="Clock" size={16} />
                                <span>{attraction?.visitDuration}</span>
                              </div>
                            </div>
                            <p className="text-muted-foreground leading-relaxed">
                              {attraction?.description}
                            </p>
                          </div>

                          {/* Highlights */}
                          <div>
                            <h4 className="font-semibold text-foreground mb-3">Highlights</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {attraction?.highlights?.slice(0, 4)?.map((highlight, index) => (
                                <div key={index} className="flex items-start space-x-2 text-sm">
                                  <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                                  <span className="text-muted-foreground">{highlight}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Best Time */}
                          <div className="p-4 bg-muted rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <Icon name="Calendar" size={16} className="text-primary" />
                              <span className="font-medium text-foreground">Best Time to Visit</span>
                            </div>
                            <p className="text-sm text-muted-foreground">{attraction?.bestTime}</p>
                          </div>

                          {/* Actions */}
                          <div className="flex space-x-4">
                            <Button
                              variant="default"
                              iconName="Navigation"
                              iconPosition="left"
                              onClick={() => {
                                const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${attraction?.coordinates?.lat},${attraction?.coordinates?.lng}`;
                                window.open(mapsUrl, '_blank');
                              }}
                            >
                              Get Directions
                            </Button>
                            <Button
                              variant="outline"
                              iconName="Eye"
                              iconPosition="left"
                              onClick={() => console.log(`Viewing details for ${attraction?.name}`)}
                            >
                              View Details
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Buttons */}
              <button
                onClick={prevAttraction}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-background/90 backdrop-blur-sm rounded-full flex items-center justify-center text-foreground hover:bg-background shadow-organic transition-micro"
              >
                <Icon name="ChevronLeft" size={20} />
              </button>
              <button
                onClick={nextAttraction}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-background/90 backdrop-blur-sm rounded-full flex items-center justify-center text-foreground hover:bg-background shadow-organic transition-micro"
              >
                <Icon name="ChevronRight" size={20} />
              </button>

              {/* Indicators */}
              <div className="flex justify-center space-x-2 mt-8">
                {attractionsData?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentAttractionIndex(index)}
                    className={`w-3 h-3 rounded-full transition-micro ${
                      index === currentAttractionIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-16 px-6 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Ready for Your Adventure?
            </h2>
            <p className="text-lg opacity-90 mb-8">
              Book your activities now and create memories that will last a lifetime
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                onClick={() => window.location.href = '/booking-engine'}
              >
                Book Activities
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                className="text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary"
                onClick={() => window.location.href = 'tel:+919876543210'}
              >
                Call Us: +91 98765 43210
              </Button>
            </div>
          </div>
        </section>
      </main>
      {/* Booking Panel */}
      <BookingPanel
        selectedActivity={selectedActivity}
        isVisible={showBookingPanel}
        onClose={() => setShowBookingPanel(false)}
        onBookingConfirm={handleBookingConfirm}
      />
      {/* Booking Summary Panel */}
      <BookingSummaryPanel
        isVisible={showBookingSummary}
        onClose={() => setShowBookingSummary(false)}
        onProceed={() => window.location.href = '/booking-engine'}
        formData={{
          activity: selectedActivity,
          selectedDate: selectedDate,
          guests: 1,
          totalAmount: selectedActivity?.price || 0
        }}
      />
      {/* Floating Action Buttons */}
      <WhatsAppChat />
      <CallButton />
    </div>
  );
};

export default ActivitiesExperiences;